import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "405",
  "slug": "longest-mountain-ranges-in-the-world-ranked",
  "title": "The Longest Mountain Ranges in the World, Ranked",
  "excerpt": "The longest mountain range on Earth is one almost nobody has seen. Here are the planet’s great ranges ranked by length, from the Andes to a 65,000-kilometre seam on the ocean floor.",
  "date": "2026-06-11",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1614586125858-e695dd97d1b6?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxhbmRlcyUyMG1vdW50YWluc3xlbnwxfDB8fHwxNzgxMTY1NTM0fDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "The longest mountain range on Earth is the mid-ocean ridge system, roughly 65,000 km long and almost entirely underwater. The longest range on land is the Andes at about 7,000 km. The Rockies, Great Dividing Range, Transantarctic Mountains, and Urals follow.",
  "content": [
    {
      "type": "paragraph",
      "text": "Ask someone to name the longest mountain range in the world and most people reach for the Himalaya or the Andes. Both are good answers — but both are wrong, because the real champion is a range almost no one has ever laid eyes on. Ranking mountain ranges by length turns up some genuine surprises, so let us count them down from the longest, starting with the one hiding under the sea."
    },
    {
      "type": "heading",
      "text": "1. The mid-ocean ridge — about 65,000 km"
    },
    {
      "type": "paragraph",
      "text": "The longest mountain range on Earth is the global mid-ocean ridge system, a continuous seam of underwater mountains that winds around the planet for roughly 65,000 kilometres — more than one and a half times the circumference of the Earth. It is where tectonic plates pull apart and fresh rock wells up from below, building a ridge along the boundary. The Mid-Atlantic Ridge is its most famous segment, running down the middle of the Atlantic, and it surfaces in a handful of places, most spectacularly in Iceland. It dwarfs every range on land, but because it sits beneath kilometres of seawater, almost nobody thinks of it as a mountain range at all."
    },
    {
      "type": "heading",
      "text": "2. The Andes — about 7,000 km"
    },
    {
      "type": "paragraph",
      "text": "On land, nothing comes close to the Andes. They run almost the entire western edge of South America, from Venezuela in the north through Colombia, Ecuador, Peru, Bolivia, Chile, and Argentina, for roughly 7,000 kilometres. They are the world's longest continental mountain range and home to the highest peaks outside Asia, including Aconcagua. The Andes are also a textbook example of a subduction range, thrown up where the oceanic Nazca Plate dives beneath the South American continent."
    },
    {
      "type": "heading",
      "text": "3. The Rocky Mountains — about 4,800 km"
    },
    {
      "type": "paragraph",
      "text": "North America's spine, the Rockies stretch some 4,800 kilometres from British Columbia in Canada down through the western United States to New Mexico. They form a major continental divide, splitting rivers that drain to the Pacific from those that drain to the Atlantic and Arctic, and their distinctive ridged texture is one of the easiest North American features to pick out from a satellite view."
    },
    {
      "type": "heading",
      "text": "4. The Great Dividing Range — about 3,500 km"
    },
    {
      "type": "paragraph",
      "text": "Australia's Great Dividing Range runs roughly 3,500 kilometres down the eastern side of the continent, from the tropical north of Queensland to Victoria in the south. It is not especially tall — its highest point, Mount Kosciuszko, is just over 2,200 metres — but in length it is one of the great ranges of the world, and it is the reason Australia's wet, green east coast is separated from the dry interior behind it."
    },
    {
      "type": "heading",
      "text": "5. The Transantarctic Mountains — about 3,500 km"
    },
    {
      "type": "paragraph",
      "text": "Cutting clear across Antarctica, the Transantarctic Mountains run roughly 3,500 kilometres and divide the continent into its eastern and western halves. They are one of the few ranges on Earth that pokes through a continental ice sheet, with peaks rising above the ice as dark, jagged nunataks. Because so much of the surrounding land is buried under kilometres of ice, this is a range defined as much by what is hidden as by what is visible."
    },
    {
      "type": "heading",
      "text": "Honourable mentions"
    },
    {
      "type": "paragraph",
      "text": "Several more ranges deserve a place on any serious list:"
    },
    {
      "type": "list",
      "items": [
        "The Ural Mountains (~2,500 km) — the traditional dividing line between Europe and Asia.",
        "The Atlas Mountains (~2,500 km) — running across Morocco, Algeria, and Tunisia in North Africa.",
        "The Himalaya (~2,400 km) — shorter than the giants above, but home to Everest and the highest peaks on the planet.",
        "The Western Ghats (~1,600 km) — hugging the western coast of India and one of the world’s great biodiversity hotspots."
      ]
    },
    {
      "type": "heading",
      "text": "How mountain ranges are even measured"
    },
    {
      "type": "paragraph",
      "text": "Ranking ranges by length is trickier than it sounds, which is why you will see different numbers in different sources. Where exactly does a range begin and end? Geologists sometimes group several connected ranges into one great chain — the American Cordillera, for instance, links the Andes and the Rockies into a single mountain system running nearly the full length of the Americas, which would top 15,000 kilometres if you counted it as one. Others split ranges into smaller named sub-ranges. The figures above use the conventional, widely cited extents for each named range, but the lesson is that 'longest' depends on where you draw the lines — much like coastlines, mountain ranges resist a single tidy number."
    },
    {
      "type": "heading",
      "text": "Why length and height are different stories"
    },
    {
      "type": "paragraph",
      "text": "It is worth separating the two questions people tend to blur together. Height is about how a range was built and how recently — young, fast-rising ranges like the Himalaya have the tallest peaks. Length is about how long the plate boundary or the buckled crust runs. That is why the Himalaya tops the height charts but does not even make the top three for length, and why a quiet underwater seam beats everything on land. The planet keeps its biggest features where most of us never look."
    }
  ]
};
